import { createSelector } from 'reselect';
import { ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { CapabilitiesType } from 'flow/types/Extension';
import { State } from 'flow/types/State';
import { Capabilities } from 'flow/utils/capability-utils';
import { calcTotalUnreadVoiceMessages } from 'flow/utils/extension-utils';
import { Map } from 'immutable';
import { extensionStatuses } from 'flow/utils';

export const selectExtensions = (state: State) =>
    state.extensions.get('extensions');

export const selectExtensionKeyFromContactKey = createSelector(
    [selectExtensions, (_state: State, contactKey: ContactKey) => contactKey],
    (extensions, contactKey) => {
        if (!contactKey) return null;
        return extensions
            ?.find(
                (extension) =>
                    extension.getIn(['contact', 'key']) === contactKey,
            )
            ?.get('key');
    },
);

// Factory that returns a fresh memoized selector. Use when the selector is
// called with different contactKey values across component instances mounted
// at the same time (e.g. rows in a list) — each instance needs its own cache,
// or memoization will thrash. Consume with useMemo(makeSelect..., []).
export const makeSelectExtensionKeyFromContactKey = () =>
    createSelector(
        [
            selectExtensions,
            (_state: State, contactKey: ContactKey) => contactKey,
        ],
        (extensions, contactKey) => {
            if (!contactKey) return null;
            return extensions
                ?.find(
                    (extension) =>
                        extension.getIn(['contact', 'key']) === contactKey,
                )
                ?.get('key');
        },
    );

export const selectExtension = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['extensions', key]);

/** Whether we hold an extension for this key at all. Selecting the boolean
 *  rather than the extension keeps a caller that only needs to know it exists
 *  from re-rendering every time that extension's data is rewritten. */
export const selectExtensionExists = (state: State, key: ExtensionKey) =>
    !!selectExtension(state, key);

export const selectExtensionContactKey = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['extensions', key, 'contact', 'key']);

export const selectExtensionIsAiCoworker = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['extensions', key, 'extensionType']) ===
    'ai_coworker';

export const selectOwnExtensionKey = (state: State) =>
    state.app.getIn(['extension', 'key']);

export const selectChatUserKey = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['extensions', key, 'chatUserKey']);

export const selectContact = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['extensions', key, 'contact']);

export const selectContactName = (state: State, key: ExtensionKey) => {
    const contact = selectContact(state, key);
    if (!contact) return '';

    // Trimmed, and each half defaulted. A contact with neither name otherwise
    // reads as " " — truthy, so every caller's fallback to a number, an email or
    // "Unknown" was unreachable — or as "undefined undefined" when a half is absent.
    const firstName = contact.get('firstName') || '';
    const lastName = contact.get('lastName') || '';

    return `${firstName} ${lastName}`.trim();
};

export const selectContactFirstName = (state: State, key: ExtensionKey) =>
    selectContact(state, key)?.get('firstName');

export const selectContactLastName = (state: State, key: ExtensionKey) =>
    selectContact(state, key)?.get('lastName');

export const selectContactFixedNumber = (state: State, key: ExtensionKey) =>
    selectContact(state, key)?.get('fixedNumber');

export const selectContactMobileNumber = (state: State, key: ExtensionKey) =>
    selectContact(state, key)?.get('mobileNumber');

export const selectContactEmail = (state: State, key: ExtensionKey) =>
    selectContact(state, key)?.get('email');

const selectUserExtensionKey = (state: State) =>
    state.app.getIn(['extension', 'key']);

const selectUserContact = (state: State) =>
    state.app.getIn(['extension', 'contact']);

const selectUserFixedCountryCode = (state: State) =>
    selectUserContact(state)?.getIn(['fixed', 'countryCode']);

const selectUserMobileCountryCode = (state: State) =>
    selectUserContact(state)?.getIn(['mobile', 'countryCode']);

const selectUserCustomerCountryCode = (state: State) => {
    const extensionKey = selectUserExtensionKey(state);
    return state.customer.getIn([extensionKey, 'country', 'countrycode']);
};

export const selectExtensionProfiles = (state: State, key: ExtensionKey) =>
    state.profiles.get(key);

export const selectUserCountryCode = (state: State) => {
    return (
        selectUserFixedCountryCode(state)?.toLowerCase() ||
        selectUserMobileCountryCode(state)?.toLowerCase() ||
        selectUserCustomerCountryCode(state)?.toLowerCase() ||
        ''
    );
};

export const selectExtensionsStatuses = (state: State) =>
    state.extensions.get('statuses');

export const selectExtensionStatus = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['statuses', key], 'unknown');

export const selectOwnExtensionStatus = (state: State) =>
    selectExtensionStatus(state, selectOwnExtensionKey(state));

export const selectExtensionActiveProfile = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['activeProfiles', key]);

export const selectExtensionActiveProfileKey = (
    state: State,
    key: ExtensionKey,
) => selectExtensionActiveProfile(state, key)?.get('key');

export const selectExtensionActiveProfileOperatorNote = (
    state: State,
    key: ExtensionKey,
) => selectExtensionActiveProfile(state, key)?.get('operatorNote', '');

export const selectExtensionActiveProfileActiveUntil = (
    state: State,
    key: ExtensionKey,
) => selectExtensionActiveProfile(state, key)?.get('activeUntil');

export const selectNextScheduledProfiles = (state: State) =>
    state.extensions.get('nextScheduledProfiles');

export const selectNextScheduledProfile = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['nextScheduledProfiles', key]);

export const selectNextScheduledProfileAvailable = (
    state: State,
    key: ExtensionKey,
) => selectNextScheduledProfile(state, key)?.get('available');

export const selectNextScheduledProfileQueueLoginList = (
    state: State,
    key: ExtensionKey,
) => selectNextScheduledProfile(state, key)?.get('queueLoginList');

export const selectActiveProfileActiveUntil = (
    state: State,
    key: ExtensionKey,
) => selectExtensionActiveProfile(state, key)?.get('activeUntil');

export const selectActiveProfileOperatorNote = (
    state: State,
    key: ExtensionKey,
) => selectExtensionActiveProfile(state, key)?.get('operatorNote');

export const selectActiveProfileDescription = (
    state: State,
    key: ExtensionKey,
) => selectExtensionActiveProfile(state, key)?.get('description');

export const selectOwnActiveProfileDescription = (state: State) =>
    selectActiveProfileDescription(state, selectOwnExtensionKey(state));

export const selectVoiceMessages = (state: State, key: ExtensionKey) =>
    state.extensions.getIn(['extensions', key, 'voiceMessages']);

export const selectOwnVoiceMessages = (state: State) =>
    selectVoiceMessages(state, selectOwnExtensionKey(state));

export const selectOwnUserHasCapability = (
    state: State,
    capability: CapabilitiesType,
) => state.app.hasIn(['extension', 'capabilities', capability]);

export const selectOwnUserHasCapabilityVideo = (state: State) =>
    selectOwnUserHasCapability(state, Capabilities.VIDEO);

export const selectOwnUserHasCapabilityVideo1on1 = (state: State) =>
    selectOwnUserHasCapability(state, Capabilities.VIDEO_1_ON_1);

export const selectOwnUserHasCapabilityCCPro = (state: State) =>
    selectOwnUserHasCapability(state, Capabilities.CC_PRO);

export const selectHasCapability = (
    state: State,
    extensionKey: ExtensionKey,
    capability: CapabilitiesType,
) =>
    state.extensions.hasIn([
        'extensions',
        extensionKey,
        'capabilities',
        capability,
    ]);

/* The two selectors above answer with hasIn, which cannot tell "does not have
 * this capability" from "has not been fetched yet". These say whether the
 * answer can be trusted, so a caller can tell a real no from a not-yet.
 *
 * Asking whether one extension has arrived would not do for the second: a
 * colleague who has been removed from the customer never arrives, so it would
 * read not-yet forever. The bulk fetch landing is what settles it. */

export const selectOwnCapabilitiesAreLoaded = (state: State) =>
    state.app.hasIn(['extension', 'capabilities']);

export const selectExtensionsCapabilitiesAreLoaded = (state: State) =>
    state.extensions.get('extensionsCapabilitiesAreLoaded', false);

export const selectHasMicrosoftContactOrCalendarCapability = (
    state: State,
    key: ExtensionKey,
) =>
    selectHasCapability(state, key, Capabilities.MICROSOFT_CALENDAR_SYNC) ||
    selectHasCapability(state, key, Capabilities.MICROSOFT_CONTACT_SYNC);

export const selectHasGoogleContactOrCalendarCapability = (
    state: State,
    key: ExtensionKey,
) =>
    selectHasCapability(state, key, Capabilities.GOOGLE_CALENDAR_SYNC) ||
    selectHasCapability(state, key, Capabilities.GOOGLE_CONTACT_SYNC);

export const selectNumberUnreadVoiceMessages = createSelector(
    [selectOwnVoiceMessages],
    (voiceMessages) => calcTotalUnreadVoiceMessages(voiceMessages),
);

export const selectExtensionLicenseName = createSelector(
    [selectExtension],
    (extension) => extension.getIn(['license', 'name'], 'flow_free'),
);

const getContactKey = (_state: State, contactKey?: ContactKey) => contactKey;

export const getExtensions = (state: State) =>
    state.extensions.get('extensions', Map());

export const getExtensionsStatuses = (state: State) =>
    state.extensions.get('statuses');

export const getExtension = (state: State, extensionKey: ExtensionKey) =>
    getExtensions(state).get(extensionKey);

export const selectExtensionDepartment = (
    state: State,
    extensionKey: ExtensionKey,
) =>
    state.extensions.getIn([
        'extensions',
        extensionKey,
        'contact',
        'department',
    ]);

export const getExtensionFromContactKey = createSelector(
    [getExtensions, getContactKey],
    (extensions, contactKey) =>
        extensions.find(
            (extension) => extension.getIn(['contact', 'key']) === contactKey,
        ),
);

export const getExtensionKeyFromContactKey = createSelector(
    [getExtensions, getContactKey],
    (extensions, contactKey) => {
        if (!contactKey) return null;
        const extension =
            extensions.find(
                (extension) =>
                    extension.getIn(['contact', 'key']) === contactKey,
            ) || Map();
        return extension.get('key');
    },
);

export const getChatUserKeyForExtensionWithContactKey = createSelector(
    [getExtensionFromContactKey],
    (extension) => extension?.get('chatUserKey'),
);

export const canSetPresenceNotification = (
    state: State,
    extensionKey?: ExtensionKey,
) => {
    if (!extensionKey) return false;

    const status = selectExtensionStatus(state, extensionKey);
    return status !== extensionStatuses.available.key;
};

export const getChatUserKeyForExtensionWithKey = (
    state: State,
    extensionKey: ExtensionKey,
) => {
    const extensions = getExtensions(state);
    return extensions.getIn([extensionKey, 'chatUserKey']);
};
